import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {HTMLElementId, TOOLTIP_VISIBLE_TIME} from '@/constants';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {TooltipsStore} from '@/stores/TooltipsStore/TooltipsStore';
import {GlobalWindow} from '@/types';
import {ClickerGameEventNames, ClickerGameEvents} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerGameEvents';

import {OnboardingService} from '../shared/OnboardingService/OnboardingService';

export class TooltipsService {
  private timeoutId: number = -1;
  private prevBatteryLevel: number | null = null;
  private idleClickCounter: number = 0;
  private isTooltipVisible: boolean = false;

  constructor(
    private readonly tooltipsStore: TooltipsStore,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly clickerGameEvents: ClickerGameEvents,
    private readonly onboardingService: OnboardingService,
    private readonly globalWindow: GlobalWindow,
  ) {}

  public init(): void {
    this.clickerGameEvents.subscribeOnEvent(ClickerGameEventNames.AnyTap, this.handleTap);
    this.clickerStateStore.subscribe(this.handleClickerStateChanged);
  }

  public showNextDailyGameTooltip(): void {
    const targetNode = this.globalWindow.document.getElementById(HTMLElementId.DailyGameRadar);
    const excludeNode = this.globalWindow.document.getElementById(HTMLElementId.GameCanvas);

    if (targetNode && excludeNode) {
      this.tooltipsStore.setActiveTooltip(HTMLElementId.DailyGameRadar, targetNode, [excludeNode]);
      this.activateHideTooltipByTimeout();
      this.activatePreventClick();
    }

    this.isTooltipVisible = true;
  }

  public showStatisticsLockedTooltip(): void {
    const targetNode = this.globalWindow.document.getElementById(HTMLElementId.ResultsBottomBarStatisticsButton);
    const excludeNode = this.globalWindow.document.getElementById(HTMLElementId.GameCanvas);

    if (targetNode && excludeNode) {
      this.tooltipsStore.setActiveTooltip(HTMLElementId.ResultsBottomBarStatisticsButton, targetNode, [excludeNode]);
      this.activateHideTooltipByTimeout();
      this.activatePreventClick();
    }

    this.isTooltipVisible = true;
  }

  private handleClickerStateChanged = (): void => {
    const currentBatteryLevel = this.clickerStateStore.getBatteryChargeLevelInPercent();
    if (this.prevBatteryLevel !== null && !ClickerStateStore.isLowBatteryLevel(currentBatteryLevel)) {
      this.prevBatteryLevel = null;
    }
  };

  private handleTap = (): void => {
    if (!this.onboardingService.isMainOnboardingCompleted()) return;

    if (ClickerStateStore.isLowBatteryLevel(this.clickerStateStore.getBatteryChargeLevelInPercent())) {
      if (this.prevBatteryLevel === null) {
        this.showLowEnergyTooltip();
      }

      if (this.clickerStateStore.isEmptyEnergy() && this.isTooltipVisible === false) {
        this.idleClickCounter++;

        if (this.idleClickCounter > 5) {
          this.showLowEnergyTooltip();

          this.idleClickCounter = 0;
        }
      }
    }
  };

  private showLowEnergyTooltip(): void {
    const nodeId = this.clickerStateStore.isEmptyCharges()
      ? HTMLElementId.RechargeWithTasksTarget
      : HTMLElementId.ClickerGameRecharge;

    const targetNode = this.globalWindow.document.getElementById(nodeId);
    const excludeNode = this.globalWindow.document.getElementById(HTMLElementId.GameCanvas);

    if (targetNode && excludeNode) {
      this.tooltipsStore.setActiveTooltip(nodeId, targetNode, [excludeNode]);
      this.activateHideTooltipByTimeout();
      this.activatePreventClick();
    }

    this.prevBatteryLevel = this.clickerStateStore.getBatteryChargeLevelInPercent();

    this.isTooltipVisible = true;
  }

  private activatePreventClick(): void {
    this.globalWindow.addEventListener('click', this.handleClick, true);
  }

  private handleClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;

    // кликнули по ноде, которая должна закрывать тултип
    // либо по какой-то другой ноде, которая не является исключением
    // тогда надо закрывать тултип
    if (this.tooltipsStore.nodeIsTarget(target) || !this.tooltipsStore.isExcludeNode(target)) {
      this.globalWindow.clearTimeout(this.timeoutId);
      this.globalWindow.removeEventListener('click', this.handleClick);
      this.tooltipsStore.hideTooltip();

      this.isTooltipVisible = false;
      this.idleClickCounter = 0;
    }
  };

  private activateHideTooltipByTimeout(): void {
    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.tooltipsStore.hideTooltip();
      this.isTooltipVisible = false;
      this.idleClickCounter = 0;
      this.globalWindow.removeEventListener('click', this.handleClick);
    }, TOOLTIP_VISIBLE_TIME);
  }
}

injected(
  TooltipsService,
  DI_TOKENS.tooltipsStore,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.clickerGameEvents,
  DI_TOKENS.onboardingService,
  DI_TOKENS.globalWindow,
);
